import { useMemo, useState } from 'react';
import { Card, Stack, Title, Text, Badge, Group, Button } from '@repletoryreach/sdk-ui';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import type { templates as templatesNs } from '@repletoryreach/sdk-api';
import { parseComponents, extractVariables } from './parseComponents';
import { useT } from '../../../../i18n/I18nProvider';

type Template = NonNullable<Awaited<ReturnType<typeof templatesNs.get>>['data']>;

const MUTED = 'var(--rr-color-text-muted, #94a3b8)';

export function VariablesTab({ template }: { template: Template }) {
  const t = useT();
  const components = useMemo(() => parseComponents(template.components), [template.components]);
  const vars = useMemo(() => extractVariables(components), [components]);
  const [samples, setSamples] = useState<Record<number, string>>({});

  const body = components.find((c) => c.type === 'BODY')?.text ?? '';
  const filled = useMemo(
    () => body.replace(/\{\{\s*(\d+)\s*\}\}/g, (_, n) => samples[Number(n)] ?? `{{${n}}}`),
    [body, samples]
  );

  if (vars.length === 0) {
    return (
      <Alert color="blue" variant="light">
        {t('templates.view.variables.none')}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Card withBorder>
        <Stack gap="sm">
          <Group justify="space-between" align="baseline">
            <Title order={4}>{t('templates.view.variables.detected', { count: vars.length })}</Title>
            <Group gap="xs">
              {vars.map((n) => (
                <Badge key={n} size="xs" variant="light" color="orange">{`{{${n}}}`}</Badge>
              ))}
            </Group>
          </Group>
          <Text fz="xs" c={MUTED}>
            {t('templates.view.variables.hint')}
          </Text>
          <Stack gap="xs">
            {vars.map((n) => (
              <TextInput
                key={n}
                label={t('templates.view.variables.label', { position: n })}
                placeholder={t('templates.view.variables.placeholder', { position: n })}
                value={samples[n] ?? ''}
                onChange={(e) =>
                  setSamples((s) => ({ ...s, [n]: (e.target as HTMLInputElement).value }))
                }
              />
            ))}
          </Stack>
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="sm">
          <Group justify="space-between" align="baseline">
            <Title order={4}>{t('templates.view.variables.filled')}</Title>
            <Button
              size="xs"
              variant="outline"
              onClick={() => {
                void navigator.clipboard.writeText(filled);
                notifySuccess(t('templates.view.variables.copied'), t('toast.copied'));
              }}
            >
              Copy
            </Button>
          </Group>
          <div
            style={{
              background: 'var(--rr-color-surface-elevated, #0f172a)',
              border: '1px solid var(--rr-color-border, #1f2937)',
              borderRadius: 8,
              padding: 16,
              whiteSpace: 'pre-wrap',
              fontSize: 14
            }}
          >
            {filled || <span style={{ color: MUTED }}>(no body text)</span>}
          </div>
        </Stack>
      </Card>
    </Stack>
  );
}
